/**
 * -----------------------------------------------------------------------------
 * Popup - Notifications / Alerts List
 * -----------------------------------------------------------------------------
 * This popup is used for showing notification list from top menu.
 */

import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useOrganizationRuleAlertListQuery } from "../../api/network/adminApiServices";
import {
  useGetNotificationListQuery,
  useMarkNotificationAsReadMutation,
} from "../../api/network/notificationApiServices";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import { NotificationListingPayload } from "../../api/types/Notifications";
import LoadingAnimation from "../../assets/svg/loadingAnimation.svg";
import svgIcons from "../../assets/svg/svgIcons";
import {
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../components/admin/formFields";
import { APP_CONFIG } from "../../constants/constants";
import {
  dbTimeToJsTime,
  formattedDateTime,
  jsTimeToDBTime,
} from "../../utils/dateTime";
import { useLoggedInUserData } from "../../utils/user";
import { NotificationTypes } from "./utils";
import Pagination, { TPaginationSelected } from "../../components/pagination";

type TModalNotificationList = {
  alertsMode?: boolean; // popup mode: notification or alert
  onClose?: () => void;
};

type NotificationListingPayloadForUI = NotificationListingPayload & {
  start_time: number;
  end_time: number;
};

type FilterType = "all" | "read" | "unread";
type TListingFilters = {
  slug: FilterType;
  title: string;
};

const page_size = APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE;

const ModalNotificationList: FC<TModalNotificationList> = ({
  alertsMode = false,
  onClose,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.notifications",
  });
  const { t: tToast } = useTranslation("translation", {
    keyPrefix: "dashboard.notifications.toast",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const [expandedView, setExpandedView] = useState(false);

  // filters
  const filters: TListingFilters[] = [
    {
      slug: "all",
      title: t("filter_all"),
    },
    {
      slug: "unread",
      title: t("filter_unread"),
    },
    {
      slug: "read",
      title: t("filter_read"),
    },
  ];

  // filter mechainism
  const [activeFilterSlug, setActiveFilterSlug] = useState<FilterType>(
    filters?.[0]?.slug
  );

  const hideModal = () => {
    // reset filters
    setExpandedView(false);
    setActiveFilterSlug(filters?.[0]?.slug);
    setNotificationParams(defaultNotificationParams);
    dispatch(
      setModalsData({
        ...modalsState,
        showNotifications: false,
        showAlertNotifications: false,
      })
    );
    if (onClose) onClose();
  };

  const [markReadAPITrigger, { isLoading }] =
    useMarkNotificationAsReadMutation();

  // fetching rules data
  const { data: dataOrgRules, isFetching: isFetchingOrgRules } =
    useOrganizationRuleAlertListQuery({
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
    });

  // fetching notification list data
  const defaultNotificationParams = {
    notify_type: alertsMode
      ? NotificationTypes.alert
      : NotificationTypes.notification,
    start_time: jsTimeToDBTime(
      new Date(new Date().setDate(new Date().getDate() - 30)).getTime()
    ),
    end_time: jsTimeToDBTime(new Date().getTime()),
    page: 1,
    page_size,
  } as NotificationListingPayloadForUI;
  const [notificationParams, setNotificationParams] = useState(
    defaultNotificationParams
  );
  const { data: dataNotification, isFetching } = useGetNotificationListQuery(
    {
      ...notificationParams,
      ...(expandedView
        ? {
            is_read:
              activeFilterSlug === "unread"
                ? 0
                : activeFilterSlug === "read"
                  ? 1
                  : undefined,
          }
        : {}),
      start_time: new Date(
        dbTimeToJsTime(notificationParams.start_time)
      ).toISOString(),
      end_time: new Date(
        dbTimeToJsTime(notificationParams.end_time)
      ).toISOString(),
    } as NotificationListingPayload,
    {
      skip: !dataOrgRules,
    }
  );
  const { count, next, previous, results } = dataNotification || {};

  const onChangeFilterRuleDescription = (selectedOption: any) => {
    setNotificationParams({
      ...notificationParams,
      rule_id: !!selectedOption?.value ? selectedOption?.value : undefined,
    });
  };

  const markAsRead = (id: number) => {
    // mark notification as read
    markReadAPITrigger(id)
      .unwrap()
      .then((res) => {
        toast.success(
          tToast(alertsMode ? "alert_marked_read" : "notification_marked_read")
        );
      })
      .catch((err) => {
        if (APP_CONFIG.DEBUG.ALL) console.log(err);

        toast.error(
          tToast(
            alertsMode
              ? "alert_marked_read_failed"
              : "notification_marked_read_failed"
          )
        );
      });
  };

  if (
    (modalsState?.showNotifications === false && alertsMode === false) ||
    (modalsState?.showAlertNotifications === false && alertsMode === true)
  )
    return null;

  return (
    <>
      {/* overlay */}
      {/* <div className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay" onClick={hideModal}></div> */}

      {/* modal */}
      <div
        className={`absolute ${expandedView ? "w-[500px] px-4 py-3" : "w-72 p-2"} max-w-[calc(100vw-10rem)] bg-white flex flex-col gap-2 top-full mt-2 right-0 rounded-lg shadow-lg z-modal`}
      >
        {/* header */}
        {expandedView && (
          <div className="flex justify-between items-center">
            <h4 className="text-[32px] font-bold">
              {alertsMode ? t("heading_alerts") : t("heading")}
            </h4>
            <button
              className="flex items-center justify-center size-10"
              type="button"
              onClick={hideModal}
            >
              <img
                src={svgIcons.CloseIcon}
                className="size-8 hover:opacity-40"
                alt="close"
              />
            </button>
          </div>
        )}

        {/* filters */}
        {expandedView && (
          <>
            <AdminFormFieldDropdown
              loadingData={isFetchingOrgRules}
              label={t("filter_rule_description")}
              id="filter_rule_description"
              name="filter_rule_description"
              options={
                dataOrgRules?.results?.map((item) => ({
                  label: item.ruleid,
                  value: item.ruleid,
                })) || []
              }
              placeholder={t("filter_rule_description_placeholder")}
              isClearable={true}
              onChange={onChangeFilterRuleDescription}
            />
            <div className="grid grid-cols-12 gap-4 mt-2">
              <AdminFormFieldInput
                customWrapperClass="col-span-6"
                label={t(`filter_date_from`)}
                type="datetime-picker"
                id={`filter_date_from`}
                name={`filter_date_from`}
                value={
                  notificationParams.start_time
                    ? dbTimeToJsTime(notificationParams.start_time)
                    : undefined
                }
                maxTime={new Date()}
                // value={values[`${field_start_time.param}`]}
                onDatepickerChange={(field_name: string, value: any) => {
                  setNotificationParams({
                    ...notificationParams,
                    start_time: jsTimeToDBTime(value),
                    end_time: null, // jsTimeToDBTime(value.getTime() + (60 * 60 * 24)),
                  } as NotificationListingPayloadForUI);
                }}
                // onBlur={handleBlur}
                size="lg"
              />
              <AdminFormFieldInput
                customWrapperClass="col-span-6"
                label={t(`filter_date_to`)}
                type="datetime-picker"
                id={`filter_date_to`}
                name={`filter_date_to`}
                value={
                  notificationParams.end_time
                    ? dbTimeToJsTime(notificationParams.end_time)
                    : undefined
                }
                maxTime={new Date()}
                onDatepickerChange={(field_name: string, value: any) => {
                  setNotificationParams({
                    ...notificationParams,
                    end_time: jsTimeToDBTime(value),
                  } as NotificationListingPayloadForUI);
                }}
                minTime={
                  notificationParams.start_time
                    ? new Date(
                        dbTimeToJsTime(
                          notificationParams.start_time + 60 * 60 * 24
                        )
                      ) // plus 1 day
                    : undefined
                }
                // onBlur={handleBlur}
                size="lg"
              />
            </div>
          </>
        )}
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="absolute top-0 right-0 text-[8px] opacity-50 pointer-events-none touch-none">
            {JSON.stringify(notificationParams, null, 2)}
          </pre>
        )}

        {/* Read / Unread Filters */}
        {expandedView && filters && filters.length > 0 && (
          <div className="mb-2">
            <div className="sm:hidden">
              <label htmlFor="Tab" className="sr-only">
                Tab
              </label>

              <select
                id="Tab"
                className="w-full rounded-md border-gray-200"
                defaultValue={activeFilterSlug}
              >
                {filters.map((filter, i) => (
                  <option
                    key={`select_${i}_${filter.slug}`}
                    value={filter.slug}
                  >
                    {filter.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="hidden sm:block">
              <nav className="flex gap-3" aria-label="Tabs">
                {filters.map((filter, i) => (
                  <a
                    className={`flex-1 Xshrink-0 border-b-2 px-1 py-2 text-sm font-semibold text-center ${filter.slug === activeFilterSlug ? `border-accent-blue-dark text-accent-blue-dark cursor-default` : `border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 cursor-pointer`}`}
                    key={`tab_${i}_${filter.slug}`}
                    onClick={() => setActiveFilterSlug(filter.slug)}
                  >
                    {filter.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* list */}
        <ul className="flex flex-col gap-2 max-h-[calc(100vh-440px)] overflow-auto text-heading-gray444">
          {isFetching && (
            <div className="col-span-12 h-20 flex items-center justify-center bg-opacity-90">
              <img
                src={LoadingAnimation}
                alt="Loading animation"
                className="w-12 h-12"
              />
            </div>
          )}
          {
            // Empty View
            !isFetching && !results?.length && (
              <li className="p-2 rounded-lg">
                <div className="text-center text-heading-gray888/60 font-bold text-sm px-8 py-6">
                  {alertsMode
                    ? t(expandedView ? "no_alerts" : "no_alerts_recent")
                    : t(
                        expandedView
                          ? "no_notifications"
                          : "no_notifications_recent"
                      )}
                </div>
              </li>
            )
          }
          {results &&
            results?.length > 0 &&
            results
              ?.slice(0, expandedView ? results.length : 3)
              ?.map((item, index) => (
                <li
                  className={`p-2 rounded-lg hover:bg-accent-blue-paleO66 relative${isFetching ? " opacity-50 pointer-events-none touch-none" : ""}`}
                  key={index}
                >
                  {/* dot */}
                  {!item.is_read && (
                    <div className="absolute top-2 right-2 size-3 bg-accent-blue-dark rounded-full"></div>
                  )}

                  {/* header */}
                  <div className="flex justify-between items-center">
                    <div className="flex flex-row gap-3 justify-normal items-center">
                      {/* <img src={svgIcons.Warning} alt="delete-notification" className="size-4" /> */}
                      <h5 className="text-sm font-semibold">{item.title}</h5>
                    </div>
                    <div className="flex flex-row gap-4 items-center justify-normal">
                      {/* Eye icon removed, instead we are having a 'Mark as read' button below */}
                      {/* {
                      !(item.is_read) && (
                        <button type="button" className="relative" onClick={() => markAsRead(item.id)}>
                          <img src={svgIcons.EyeOpen} alt="view-notification" className="size-4 translate-y-[1px]" />
                        </button>
                      )
                    } */}
                      {/* <button type="button">
                      <img src={svgIcons.Delete} alt="delete-notification" className="size-4" />
                    </button> */}
                    </div>
                  </div>

                  {/* date */}
                  <div className="flex-col text-heading-gray666 text-[12px] mt-1">
                    {formattedDateTime(
                      item.created_at,
                      "MMM dd, yyyy - h:mm aaa"
                    )}
                  </div>

                  {/* description */}
                  <div className="text-heading-gray666 text-[12px] mt-2">
                    <p className={expandedView ? "" : `line-clamp-3`}>
                      {item.message}
                    </p>
                  </div>

                  {/* dot */}
                  {!item.is_read && (
                    <button
                      type="button"
                      className="mt-1 text-xs text-accent-blue-bright underline"
                      onClick={() => markAsRead(item.id)}
                    >
                      {t("mark_as_read")}
                    </button>
                  )}
                </li>
              ))}
        </ul>

        {expandedView && (
          <Pagination
            isLoading={isFetching}
            pageSize={page_size}
            handlePageSizeChange={(e: TSelectboxOption | null) => {}}
            totalPages={
              dataNotification?.count
                ? Math.ceil(
                    dataNotification?.count / notificationParams.page_size
                  )
                : 1
            }
            forcePage={notificationParams.page - 1}
            handlePageClick={(data: TPaginationSelected) => {
              setNotificationParams({
                ...notificationParams,
                page: data?.selected + 1,
              });
              // dispatch(setListingQueryParams({ ...listingQueryParams, users: { ...orgUsersQueryParams, page: data?.selected + 1 } }));
            }}
            onlyPageChange={true}
            size={1}
            wrapperClass={`flex my-1 items-center`}
          />
        )}

        {!expandedView /*&& results && results.length > 0*/ && (
          <>
            <hr />
            <AdminFormFieldSubmit
              label={t("view_all")}
              onClick={() => setExpandedView(true)}
            />
          </>
        )}
      </div>
    </>
  );
};
export default ModalNotificationList;
